import React from 'react'
import { useTranslation } from 'react-i18next'
import { RouteComponentProps } from 'react-router-dom'
import { Ranking } from '~/models'
import { rankingsStore } from '~/stores'
import { memo, observer } from '~/ui/component'
import { Center, EmptyOrFetching, Label, List, ListBar } from '~/ui/components'
import { AppLayoutConfig } from '~/ui/layouts'
import { colors, createUseStyles, layout, ThemeProvider, useTheme } from '~/ui/styling'
import { Avatar } from '../media'

export interface RankingsParams {
  competitionID: string
}

export type Props = RouteComponentProps<RankingsParams>

const RankingsScreen = observer('RankingsScreen', (props: Props) => {

  const {competitionID} = props.match.params

  const [t] = useTranslation('participants')

  const rankingsEndpoint = rankingsStore.rankings

  const fetch = React.useCallback(() => {
    rankingsStore.fetch(competitionID)
  }, [competitionID])

  React.useEffect(fetch, [fetch])

  const fetchStatus = rankingsEndpoint?.fetchStatus ?? 'idle'

  //------
  // Rendering

  function render() {
    return (
      <>
        <AppLayoutConfig
          title={rankingsEndpoint?.meta?.competitionCaption ?? null}
        />
        <List
          data={rankingsEndpoint?.data ?? []}
          renderItem={renderRanking}
          EmptyComponent={renderEmpty}
          contentPadding={layout.padding.s}
          itemGap={layout.padding.xs}
          scrollable
        />
      </>
    )
  }

  const renderEmpty = React.useCallback(() => {
    return (
      <EmptyOrFetching
        flex
        status={fetchStatus}
        {...t('profile.rankings.empty')}
      />
    )
  }, [fetchStatus, t])

  const renderRanking = React.useCallback((ranking: Ranking) => {
    return (
      <RankingBar ranking={ranking}/>
    )
  }, [])

  return render()

})

export default RankingsScreen

export interface RankingBarProps {
  ranking: Ranking
}

const RankingBar = memo('RankingBar', (props: RankingBarProps) => {

  const {ranking} = props

  const theme = useTheme()

  //------
  // Rendering

  const $ = useStyles()

  function render() {
    return (
      <ListBar
        image={renderAvatar()}
        caption={ranking.player.name}
        accessory={renderScore()}
        selected={ranking.isMe}
      />
    )
  }

  function renderAvatar() {
    return (
      <Center classNames={$.rankingAvatar}>
        <Avatar
          source={ranking.player.photoURL}
          size={avatarSize}
          firstName=''
        />
        {renderRank()}
      </Center>
    )
  }

  function renderRank() {
    return (
      <ThemeProvider dark>
        <Center classNames={$.rank}>
          <Label h3>
            {ranking.rank}
          </Label>
        </Center>
      </ThemeProvider>
    )
  }

  function renderScore() {
    return (
      <Label bold color={theme.semantic.primary}>
        {ranking.normalized?.formatted ?? ranking.score.formatted}
      </Label>
    )
  }

  return render()

})

export const avatarSize = layout.icon.xxl

const useStyles = createUseStyles({
  RankingsScreen: {
    flex: 1,
  },

  rankingAvatar: {
    position: 'relative',
  },

  rank: {
    ...layout.overlay,
    background: colors.shim.dark,
    borderRadius: avatarSize.height / 2,
  },
})